import { useState } from "react";
import { Link } from "react-router";
import { type Theme } from "#app/utils/theme.server.ts";
import { ThemeSwitch } from "../routes/resources+/theme-switch.tsx";
import { useOptionalUser, userHasRole, userHasPermission } from "../utils/user.ts";
import { AlphabetDropdown } from "./alphabet-dropdown.tsx";
import Logo from "./Logo.tsx";
import { NotificationBell } from "./notification-bell.tsx";
import { Button } from "./ui/button.tsx";
import { Icon } from "./ui/icon.tsx";
import { UserDropdown } from "./user-dropdown.tsx";

interface NavbarProps {
  searchBar?: React.ReactNode;
  userPreference?: Theme | null;
}

export function Navbar({ searchBar, userPreference }: NavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const user = useOptionalUser();
  const isAdmin =
    userHasRole(user ?? null, "admin") ||
    userHasPermission(user ?? null, "read:moderation:any");

  return (
    <header className="bg-white dark:bg-card border-b border-gray-200 dark:border-border">
      <div className="container py-4">
        <nav className="flex items-center justify-between gap-6 xl:gap-10">
          {/* Logo */}
          <Link to="/" className="flex items-center text-foreground">
            <Logo size={32} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            <AlphabetDropdown />
            <Link
              to="/word-of-the-day"
              className="text-foreground hover:text-blue-600 font-medium"
            >
              WOTD
            </Link>
            <Link
              to="/blog"
              className="text-foreground hover:text-blue-600 font-medium"
            >
              Blog
            </Link>
            <Link
              to="/random"
              className="text-foreground hover:text-blue-600 font-medium"
            >
              Random
            </Link>
            <Link
              to="/feedback"
              className="text-foreground hover:text-blue-600 font-medium"
            >
              Feedback
            </Link>
          </div>

          {/* Desktop Search */}
          <div className="hidden xl:block max-w-sm flex-1">
            {searchBar}
          </div>

          {/* Desktop Auth + Mobile Menu Button */}
          <div className="flex items-center space-x-4">
            <div className="hidden lg:flex items-center space-x-4">
              {user ? (
                <>
                  <Button asChild variant="default" size="sm">
                    <Link to="/new">Add Word</Link>
                  </Button>
                  {isAdmin && <NotificationBell />}
                  <UserDropdown />
                </>
              ) : (
                <>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/new">Add Word</Link>
                  </Button>
                  <Button asChild variant="default" size="sm">
                    <Link to="/login">Sign In</Link>
                  </Button>
                </>
              )}
              <ThemeSwitch userPreference={userPreference} />
            </div>

            {/* Mobile menu button */}
            <Button
              variant="default"
              size="icon"
              className="lg:hidden hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <Icon
                name={isMobileMenuOpen ? "cross-1" : "hamburger-menu"}
                size="lg"
                className="text-primary-foreground"
              />
            </Button>
          </div>
        </nav>

        {/* Mobile Search */}
        <div className="xl:hidden mt-4">{searchBar}</div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-200 dark:border-border bg-white dark:bg-card">
          <div className="container py-4 space-y-4">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-foreground hover:text-blue-600 font-medium py-2"
            >
              Home
            </Link>

            <div className="py-2">
              <div className="text-foreground font-medium mb-2">
                Browse by Letter
              </div>
              <div className="grid grid-cols-7 gap-2">
                {[
                  "A",
                  "B",
                  "C",
                  "D",
                  "E",
                  "F",
                  "G",
                  "H",
                  "I",
                  "J",
                  "K",
                  "L",
                  "M",
                  "N",
                  "O",
                  "P",
                  "Q",
                  "R",
                  "S",
                  "T",
                  "U",
                  "V",
                  "W",
                  "X",
                  "Y",
                  "Z",
                  "#",
                ].map((letter) => (
                  <Link
                    key={letter}
                    to={`/browse/${letter.toLowerCase()}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex h-8 w-8 items-center justify-center rounded bg-muted text-xs font-medium text-muted-foreground hover:bg-blue-600 hover:text-white"
                  >
                    {letter}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/word-of-the-day"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-foreground hover:text-blue-600 font-medium py-2"
            >
              Word of the Day
            </Link>
            <Link
              to="/blog"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-foreground hover:text-blue-600 font-medium py-2"
            >
              Blog
            </Link>
            <Link
              to="/random"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-foreground hover:text-blue-600 font-medium py-2"
            >
              Random
            </Link>

            <Link
              to="/feedback"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-foreground hover:text-blue-600 font-medium py-2"
            >
              Leave Feedback
            </Link>

            <div className="pt-4 border-t border-gray-200 dark:border-border space-y-2">
              {user ? (
                <>
                  <Button
                    asChild
                    variant="default"
                    size="sm"
                    className="w-full"
                  >
                    <Link to="/new" onClick={() => setIsMobileMenuOpen(false)}>
                      Add Word
                    </Link>
                  </Button>
                  {isAdmin && <NotificationBell />}
                  <UserDropdown />
                </>
              ) : (
                <>
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <Link to="/new" onClick={() => setIsMobileMenuOpen(false)}>
                      Add Word
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="default"
                    size="sm"
                    className="w-full"
                  >
                    <Link
                      to="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Sign In
                    </Link>
                  </Button>
                </>
              )}

              <div className="flex justify-center pt-2">
                <ThemeSwitch userPreference={userPreference} />
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
